import { Dispatch } from 'react-redux';

import { StoreState } from '../../../reducers';
import { Action } from '../../../actions';
import { EditorType } from '../constants';
import { getResourceMeta, isEditorOpened } from './utils';
import { setEditorType, setVersion, setIsEditorLoaded, setLabel } from './ui';
import { setThumbnails } from './thumbnails';
import { setPageSpreads, savePage } from './pagesController';
import { setConfigsData, setEditableTextFieldsData, saveConfigs } from './configs';
import { setThemes, selectTheme } from './themes';
import { EditableItemsData, setEditableItemsData } from './editableItems';
import { setPreprodPublicationData, getPreprodPublicationUrl, getPublishedPublicationUrl } from './preprodPublication';
import { getExternalResourcesData, resetExternalResourcesLoading, setExternalResourcesData } from './externalResources';
import { updateSpreadBackgroundServiceData } from './spreadBackground';
import { ConfigsData, PageData } from '../reducer';
import * as selectors from '../selectors';
import * as helpers from '../utils/helpers';
import htmlEditorUtils from '../utils/htmlEditor';
import indesignEditorUtils from '../utils/indesignEditor';
import { getIsAccountManagerOrAdmin } from '../../ProfilePage/selectors';
import { createNotification } from '../../NotificationBar/actions';
import { Api } from '../../../typings/api';
import ApiUtils from '../../../utils/api';
import RoutesUtils from '../../../utils/routes';
import DriveItemUtils from '../../../utils/driveItem';
import { getAspDriveItem } from '../../DriveItemList/actions';

export enum SourcesActionKeys {
	SET_DATA = '@@editor/sources/SET_DATA',
	SET_RESOURCES_DATA = '@@editor/sources/SET_RESOURCES_DATA',
	SET_PARENT_FOLDER_ID = '@@editor/sources/SET_PARENT_FOLDER_ID',
	SET_PAGE_DATA = '@@editor/sources/SET_PAGE_DATA',
	ADD_PAGE = '@@editor/sources/ADD_PAGE',
	SET_PUBLISH_PRICE = '@@editor/sources/SET_PUBLISH_PRICE',
}

interface ResourcesData {
	css?: string;
	themesCss?: string;
	scripts?: string;
}

interface SetDataAction extends Action {
	type: SourcesActionKeys.SET_DATA;
	editorType: EditorType;
	data: Api.IndesignEditorGeneralData | Api.HtmlEditorData;
}

interface SetParentFolderIdAction extends Action {
	type: SourcesActionKeys.SET_PARENT_FOLDER_ID;
	id: number;
}

interface SetResourcesDataAction extends Action {
	type: SourcesActionKeys.SET_RESOURCES_DATA;
	data: ResourcesData;
}

interface SetPageDataAction extends Action {
	type: SourcesActionKeys.SET_PAGE_DATA;
	data: PageData;
}

interface AddPageAction extends Action {
	type: SourcesActionKeys.ADD_PAGE;
	page: number;
	width?: number;
	height?: number;
}

interface SetPublishPriceAction extends Action {
	type: SourcesActionKeys.SET_PUBLISH_PRICE;
	data: Api.PublishPrice;
}

export type SourcesAction =
	SetDataAction |
	SetResourcesDataAction |
	SetParentFolderIdAction |
	SetPageDataAction |
	AddPageAction |
	SetPublishPriceAction;

const setData = (editorType: EditorType, data: Api.IndesignEditorGeneralData | Api.HtmlEditorData): SetDataAction => ({
	type: SourcesActionKeys.SET_DATA,
	editorType,
	data,
});

export const setResourcesData = (data: ResourcesData): SetResourcesDataAction => ({
	type: SourcesActionKeys.SET_RESOURCES_DATA,
	data,
});

export const setParentFolderId = (id: number): SetParentFolderIdAction => ({
	type: SourcesActionKeys.SET_PARENT_FOLDER_ID,
	id,
});

export const setPageData = (data: PageData): SetPageDataAction => ({
	type: SourcesActionKeys.SET_PAGE_DATA,
	data,
});

export const addPage = (page: number, width?: number, height?: number): AddPageAction => ({
	type: SourcesActionKeys.ADD_PAGE,
	page,
	width,
	height,
});

export const getData = (
	clientId: number,
	resourceId: number,
	shouldLoadSpreadHtml: boolean = true,
	shouldUpdateVersion: boolean = true,
	shouldUpdatePreprodPublicationUrl: boolean = false,
	preprodPublicationData?: Api.IndesignEditorData,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const resource = await dispatch(getResourceMeta(resourceId));
		const { editor: { parentFolderId } } = getState();

		if (!parentFolderId) {
			dispatch(setParentFolderId(DriveItemUtils.getParentFolderId(resource)));
		}

		const isEditableComponent = resource.type === Api.DriveItemType.Component && resource.componentType === Api.ComponentType.ZIP_EDITABLE;
		const editorType = isEditableComponent
			? EditorType.HTML
			: EditorType.INDESIGN;

		dispatch(setEditorType(editorType));

		if (editorType === EditorType.HTML) {
			await dispatch(getHtmlEditorData(clientId, resourceId));
		} else {
			await dispatch(getIndesignEditorData(
				resource,
				clientId,
				resourceId,
				shouldLoadSpreadHtml,
				shouldUpdateVersion,
				shouldUpdatePreprodPublicationUrl,
				preprodPublicationData,
			));
		}
	} catch (error) {
		dispatch(createNotification('error.fetch_editor_data_error'));
	}
};

const getHtmlEditorData = (clientId: number, resourceId: number) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		const data = await ApiUtils.getHtmlEditorData(clientId, resourceId);
		const pagesData: Api.EditorThumbnailData[] = [{
			label: data.label,
			position: 1,
		}];

		if (!isEditorOpened()) {
			return;
		}

		dispatch(setData(EditorType.HTML, data));
		dispatch(setLabel(data.label));
		// is used for adding as hash to thumbnail urls to avoid browser cache
		dispatch(setVersion(Date.now()));
		dispatch(setThumbnails(pagesData));

		await dispatch(getPageHtml(1, resourceId, EditorType.HTML));

		if (isEditorOpened()) {
			dispatch(setIsEditorLoaded(true));
		}
	} catch (error) {
		dispatch(createNotification('error.fetch_editor_data_error'));
	}
};

const getIndesignEditorData = (
	resource: Api.DriveItem,
	clientId: number,
	resourceId: number,
	shouldLoadSpreadHtml: boolean = true,
	shouldUpdateVersion: boolean = true,
	shouldUpdatePreprodPublicationUrl: boolean = false,
	preprodPublicationData?: Api.IndesignEditorData,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const isEpubPreview = resource.format === Api.PublicationFormat.EPUB_PREVIEW;
		const {
			driveList: { driveItems },
			editor: { isAccessibleVersion },
		} = getState();
		const { originalGId } = helpers.getClientAndResourceIds();
		const driveItem = isEpubPreview ? driveItems[originalGId] : resource;
		const isAccountManagerOrAdmin = getIsAccountManagerOrAdmin(getState());

		if (!DriveItemUtils.isEditable(driveItem, isAccountManagerOrAdmin)) {
			return RoutesUtils.redirectToParentDir(DriveItemUtils.getParentFolderId(resource));
		}

		const allowPreview = !isAccessibleVersion;
		const editorData = preprodPublicationData || await ApiUtils.getIndesignEditorData(clientId, resourceId, allowPreview);
		const aspDriveItem = await dispatch(getAspDriveItem(originalGId));
		const { enrichmentInfo, pagesData } = editorData;
		enrichmentInfo.invoiceStatus = aspDriveItem && aspDriveItem.invoiceStatus;

		if (!isEditorOpened()) {
			return;
		}

		const { globalId: tempGId } = enrichmentInfo;
		const isPreprodPublication = tempGId !== resourceId;

		if (isPreprodPublication) {
			dispatch(setPreprodPublicationData({
				tempGId,
				lastUpdateDate: driveItem.lastUpdateDate,
			}));
			dispatch(getData(
				clientId,
				tempGId,
				shouldLoadSpreadHtml,
				shouldUpdateVersion,
				shouldUpdatePreprodPublicationUrl,
				editorData,
			));
			return;
		}

		const { format, totalPages } = enrichmentInfo;
		const isPdfProposal = format === Api.PublicationFormat.PDF_EDITABLE;

		if (isPdfProposal) {
			enrichmentInfo.nonEpubPages = Array.from({ length: totalPages }, (v, i) => i + 1);
		}

		dispatch(setData(EditorType.INDESIGN, enrichmentInfo));

		const { label } = driveItems[originalGId];
		dispatch(setLabel(label));

		if (shouldUpdateVersion) {
			// is used for adding as hash to thumbnail urls to avoid browser cache
			dispatch(setVersion(Date.now()));
		}

		dispatch(setThumbnails(pagesData.map(({ label, position, extPage }) => ({ label, position, extPage }))));

		if (isPdfProposal && isEditorOpened()) {
			dispatch(setPageSpreads(pagesData.map(page => ({ showAlone: false })), enrichmentInfo.dimensions));
			dispatch(setIsEditorLoaded(true));
		}

		if (!isPdfProposal) {
			const { cssPath, themesCssPath, jsPath, configsPath } = enrichmentInfo;

			await dispatch(getResourcesData(resourceId, [
				cssPath,
				themesCssPath,
				jsPath,
				configsPath,
			]));

			if (isEditorOpened()) {
				dispatch(setIsEditorLoaded(true));
			}

			if (shouldLoadSpreadHtml) {
				const {
					editor: {
						pagesController: { spreads, currentSpreadIndex },
					},
				} = getState();
				const pages = spreads[currentSpreadIndex];

				await Promise.all(pages.map(page => dispatch(getPageHtml(page, resourceId))));
			}
		}

		if (!DriveItemUtils.isProposal(format)) {
			const { tempGId, originalGId } = helpers.getClientAndResourceIds();
			const isPublished = tempGId !== originalGId;

			await dispatch(getPreprodPublicationUrl(clientId, tempGId, label, shouldUpdatePreprodPublicationUrl));

			if (isPublished) {
				await dispatch(getPublishedPublicationUrl(originalGId));
			}
		}
	} catch (error) {
		dispatch(createNotification('error.fetch_editor_data_error'));
	}
};

const getResourcesData = (resourceId: number, paths: (string | undefined)[]) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const {
			editor: {
				isAccessibleVersion,
				paths: { accessibleUrl }
			},
		} = getState();

		let resourceStyle = '';

		if (accessibleUrl) {
			resourceStyle = await ApiUtils.getResourceStyle(accessibleUrl);
		}

		const promises: Promise<string>[] = paths.map(path => (
			path ? ApiUtils.getEditorResource(resourceId, path) : Promise.resolve('')
		));

		const [css, themesCss, scripts, configs] = await Promise.all(promises);
		const resourceCss = isAccessibleVersion ? resourceStyle : css;

		const { galleryInfos: { config } } = getState();

		if (!isEditorOpened()) {
			return;
		}

		dispatch(setResourcesData({
			css: indesignEditorUtils.getParsedCss(config, resourceCss, resourceId),
			themesCss,
			scripts: scripts && indesignEditorUtils.getModifiedScripts(scripts),
		}));

		const parsedConfigs: ConfigsData = JSON.parse(configs);
		dispatch(setConfigsData(parsedConfigs));
		dispatch(setPageSpreads(parsedConfigs.pagesData));

		if (themesCss) {
			const {
				themes,
				selectedThemeId,
			} = indesignEditorUtils.getThemesAndSelectedThemeId(themesCss);

			if (themes && selectedThemeId) {
				dispatch(setThemes(themes));
				dispatch(selectTheme(selectedThemeId, false));
			}
		}
	} catch (error) {
		dispatch(createNotification('error.fetch_editor_data_error'));
	}
};

export const getPageHtml = (
	page: number,
	resourceId: number,
	editorType: EditorType = EditorType.INDESIGN,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const {
		editor: {
			paths: {
				pagePath,
				accessiblePagePath,
			},
			previewPages,
			isAccessibleVersion,
		},
	} = getState();

	const isPreviewPage = previewPages.includes(page);
	if (isPreviewPage) {
		return;
	}
	const path = !!accessiblePagePath && isAccessibleVersion ? accessiblePagePath : pagePath;

	try {
		const modifiedPagePath = editorType === EditorType.HTML
			? pagePath
			: helpers.getPageHtmlPath(path, page);

		const pageHtml = await ApiUtils.getEditorResource(resourceId, modifiedPagePath);

		if (!isEditorOpened()) {
			return;
		}

		if (editorType === EditorType.HTML) {
			dispatch(parseHtmlEditorPage(page, pageHtml));
		} else {
			const {
				editor: {
					configs: { pagesData },
				},
			} = getState();
			const { cssRelUrl } = pagesData[page - 1];
			const externalId = cssRelUrl ? helpers.getNumberFromString(cssRelUrl) : undefined;

			if (externalId) {
				await dispatch(getExternalPageResources(page, externalId));
			}

			dispatch(parseIndesignEditorPage(page, pageHtml, resourceId, externalId));
		}
	} catch (error) {
		dispatch(createNotification('error.fetch_editor_data_error'));
	}
};

const parseHtmlEditorPage = (
	page: number,
	pageHtml: string,
) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const isEditable = selectors.isEditablePage(getState(), page);
	const isAccountManagerOrAdmin = getIsAccountManagerOrAdmin(getState());
	const {
		html,
		defaultHtml,
		fontsPath,
		googleFontsPath,
		editableTextFields,
		editableMedia,
		editableLinks,
		isError,
	} = htmlEditorUtils.getModifiedPageData(page, pageHtml, isEditable, isAccountManagerOrAdmin);

	if (isError) {
		return dispatch(createNotification('error.parse_editor_data_error'));
	}

	if (isEditable && !htmlEditorUtils.isModifiedFromBackoffice(pageHtml)) {
		try {
			// we need to save html with set ids even user hasn't modified anything
			// otherwise next time user goes to this page, new ids will be set and
			// it will break editor
			const { clientId, tempGId: resourceId } = helpers.getClientAndResourceIds();
			ApiUtils.saveHtmlEditorPage(clientId, resourceId, defaultHtml);
		} catch (error) {
			dispatch(createNotification('error.parse_editor_data_error'));
		}
	}

	dispatch(setPageData({
		page,
		defaultHtml,
		html,
		fontsPath,
		googleFontsPath,
	}));

	if (isEditable) {
		if (editableTextFields.items.length > 0) {
			dispatch(setEditableTextFieldsData(page, editableTextFields.editableData));
		}

		const editableItemsData: EditableItemsData = {
			editableTextFields: editableTextFields.items,
			editableMedia,
			editableLinks,
		};
		const hasSomeEditableItems = Object.values(editableItemsData).some(data => data && data.length > 0);

		if (hasSomeEditableItems) {
			dispatch(setEditableItemsData(page, editableItemsData));
		}
	}
};

export const parseIndesignEditorPage = (
	page: number,
	pageHtml: string,
	resourceId: number,
	externalId?: number,
) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const {
		editor: {
			resources: { css },
			configs: {
				generalData: {
					editablePublication: {
						[page]: { modifiedFromBackoffice = false } = {},
					},
				},
				pagesData,
			},
			pagesController: { totalPages, spreads, currentSpreadIndex },
			isAccessibleVersion,
		},
		galleryInfos: { config },
	} = getState();

	const { height: pageHeightFromConfig, cssRelUrl, themesCssRelUrl } = pagesData[page - 1];
	const isEditable = selectors.isEditablePage(getState(), page) || isAccessibleVersion;
	const isAccountManagerOrAdmin = getIsAccountManagerOrAdmin(getState());
	const pageIndexInSpread = !isAccessibleVersion ? spreads[currentSpreadIndex].findIndex(spreadPage => spreadPage === page) : -1;

	const {
		html,
		width,
		height,
		editableTextFields,
		editableMedia,
		editableLinks,
		editableButtons,
		editableIframes,
		editableEnrichments,
		editableSliders,
		themedItems,
		duplicatedItemsInSpread,
		isError,
		backgroundDeps,
	} = indesignEditorUtils.getModifiedPageData(
		page,
		pageIndexInSpread,
		pageHtml,
		css,
		config,
		resourceId,
		isEditable,
		modifiedFromBackoffice,
		isAccountManagerOrAdmin,
		totalPages,
		spreads,
		pageHeightFromConfig,
		cssRelUrl,
		themesCssRelUrl,
		isAccessibleVersion,
	);

	if (isError) {
		return dispatch(createNotification('error.parse_editor_data_error'));
	}

	dispatch(setPageData({
		page,
		defaultHtml: pageHtml,
		html,
		width,
		height,
		externalId,
	}));

	if (isEditable) {
		const hasEditableTextFieldsData = editableTextFields.editableData && Object.values(editableTextFields.editableData).some((set: Set<string>) => set.size > 0);

		if (!modifiedFromBackoffice && hasEditableTextFieldsData) {

			dispatch(setEditableTextFieldsData(page, editableTextFields.editableData!));
			dispatch(saveConfigs());
		}

		const editableItemsData: EditableItemsData = {
			editableTextFields: editableTextFields.items,
			editableMedia,
			editableLinks,
			editableButtons,
			editableIframes,
			editableEnrichments,
			editableSliders,
			themedItems,
			duplicatedItemsInSpread,
		};
		const hasSomeEditableItems = Object.values(editableItemsData).some(data => data && data.length > 0);

		if (hasSomeEditableItems) {
			dispatch(setEditableItemsData(page, editableItemsData));

			// e.g., to convert inDesign gotoPage links (change target from page to spread number)
			const isLinkCorrectionNeeded = [
				...editableLinks,
				...editableButtons,
			].some(({ shouldCorrect }) => shouldCorrect);

			if (isLinkCorrectionNeeded) {
				dispatch(savePage(page));
			}
		}

		if (backgroundDeps) {
			dispatch(updateSpreadBackgroundServiceData(page, { deps: backgroundDeps }, false, false));
		}
	}
};

const getExternalPageResources = (page: number, externalId: number) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const {
			editor: {
				configs: { pagesData },
				externalResources,
			},
			galleryInfos: { config },
		} = getState();

		const isExternalResourcesLoaded = !!externalResources[externalId] && externalResources[externalId].css;
		const isExternalResourcesLoading = !!externalResources[externalId] && externalResources[externalId].isLoading;

		if (!isExternalResourcesLoaded && !isExternalResourcesLoading) {
			dispatch(getExternalResourcesData(externalId));
			const { cssRelUrl, jsRelUrl, themesCssRelUrl } = pagesData[page - 1];
			const promises: Promise<string>[] = [cssRelUrl, jsRelUrl, themesCssRelUrl].map(path => (
				path
					? ApiUtils.getEditorResource(undefined, path.replace(helpers.mediaParamRegex, ''))
					: Promise.resolve('')
			));
			const [css, scripts, themesCss] = await Promise.all(promises);
			const parsedCss = indesignEditorUtils.getParsedCss(config, css, externalId, cssRelUrl);
			const parsedScripts = scripts && indesignEditorUtils.getModifiedScripts(scripts);

			dispatch(setExternalResourcesData(externalId, {
				css: parsedCss,
				scripts: parsedScripts,
				themesCss,
			}));
		}
	} catch (error) {
		dispatch(resetExternalResourcesLoading(externalId));
		dispatch(createNotification('error.fetch_editor_data_error'));
	}
};

export const getPublishPrice = (globalId: number) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		const data = await ApiUtils.getPublishPrice(globalId);

		if (!isEditorOpened()) {
			return;
		}

		dispatch(setPublishPrice(data));
	} catch (error) {
		dispatch(createNotification('error.fetch_editor_data_error'));
	}
};

export const setPublishPrice = (data: Api.PublishPrice): SetPublishPriceAction => ({
	type: SourcesActionKeys.SET_PUBLISH_PRICE,
	data,
});



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/actions/sources.ts